import { useEffect, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getDeviceDistribution } from '../../../api/devices'
import { listEarthquakes } from '../../../api/earthquakes'
import { getRescueFlow } from '../../../api/rescue'
import { hasPermission } from '../../../lib/authz'
import { qk } from '../../../lib/query-keys'
import type { TrackTask } from '../../../lib/track'
import { useL7Map } from '../../../hooks/useL7Map'
import { useMapLayers } from '../../../hooks/useMapLayers'
import { useTokenClaims } from '../../../stores/auth'
import { useUiStore } from '../../../stores/ui'
import { MapLayerSwitcher } from './MapLayerSwitcher'
import { WaveArrivals } from './WaveArrivals'

export function CommandMap() {
  const activeLayers = useUiStore((s) => s.activeLayers)
  const selectedEventId = useUiStore((s) => s.selectedEventId)
  const mapFocus = useUiStore((s) => s.mapFocus)
  const containerRef = useRef<HTMLDivElement>(null)
  const claims = useTokenClaims()

  const { scene, ready } = useL7Map(containerRef)

  const distQuery = useQuery({ queryKey: ['devices', 'distribution'], queryFn: getDeviceDistribution, staleTime: 30_000, retry: 1 })
  const quakeQuery = useQuery({
    queryKey: ['earthquakes', 'map', 'active'],
    queryFn: () => listEarthquakes({ status: 'all', page: 1, pageSize: 50 }),
    staleTime: 15_000,
    refetchInterval: 60_000,
    retry: 1,
  })

  // 选中事件的救援流向：志愿者轨迹叠加在主地图（需 tasks.read 权限）
  const flowQuery = useQuery({
    queryKey: qk.rescueFlow(selectedEventId ?? 'none'),
    queryFn: () => getRescueFlow(selectedEventId!),
    enabled: Boolean(selectedEventId) && hasPermission(claims, 'tasks.read'),
    refetchInterval: 30_000,
    retry: 1,
  })
  const tracks = useMemo<TrackTask[]>(
    () => (flowQuery.data?.tasks ?? []).filter((t) => t.track.length >= 2),
    [flowQuery.data],
  )

  // 选中事件时聚焦地图
  useEffect(() => {
    if (!selectedEventId) return
    const quake = quakeQuery.data?.items.find((e) => e.eventId === selectedEventId)
    if (quake) {
      useUiStore.getState().focusMap(quake.epicenterLat, quake.epicenterLon, 8)
    }
  }, [selectedEventId, quakeQuery.data])

  useMapLayers(scene, ready, {
    devices: distQuery.data ?? [],
    earthquakes: quakeQuery.data?.items ?? [],
    selectedEventId,
    focus: mapFocus,
    tracks,
  }, activeLayers)

  return (
    <div className="panel-glass relative h-full min-h-[460px] overflow-hidden rounded-xl">
      <div ref={containerRef} className="absolute inset-0" aria-label="地理态势地图" role="application" />
      <MapLayerSwitcher />
      <WaveArrivals />
      {distQuery.isError && quakeQuery.isError && (
        <div className="absolute inset-x-0 bottom-3 z-10 flex justify-center">
          <span className="rounded-md bg-black/50 px-3 py-1.5 text-[11px] text-warning backdrop-blur">
            后端服务未连接，地图数据不可用
          </span>
        </div>
      )}
    </div>
  )
}
